import { WSQueryCache } from "@ws-react-query";
import {
  BulkActionOnProgress,
  BulkActionProgress,
  bulkActionFactory,
  useForegroundBulkAction
} from "../../BulkActions";
import {
  QUERY_PAYABLES as QUERY_PAYABLES_NEW,
  QUERY_PAYABLES_SUMMARY
} from "../../../query/payables/keys";
import {
  QUERY_PAYABLE,
  QUERY_PAYABLES as QUERY_PAYABLES_LEGACY
} from "../../../query/payments/keys";
import { paymentsService } from "../../../services/payments";
import { Payable } from "../../../services/api/payments/payables";

const invalidateAfterMutation = (successItems: Payable[]) => {
  WSQueryCache.invalidateQueries(QUERY_PAYABLES_NEW);
  WSQueryCache.invalidateQueries(QUERY_PAYABLES_LEGACY);
  WSQueryCache.invalidateQueries(QUERY_PAYABLES_SUMMARY);
  successItems.forEach(payable => {
    if (payable.payableId) {
      WSQueryCache.invalidateQueries([QUERY_PAYABLE, payable.payableId]);
    }
  });
};

const processDeletePayables = (
  items: Payable[],
  onProgress: BulkActionOnProgress,
  onSuccess?: () => void
): Promise<BulkActionProgress> => {
  const bulkAction = bulkActionFactory<Payable>({
    onProgress,
    action: async item => {
      if (!item.payableId) {
        throw new Error("Payable is missing payableId");
      }
      if (item.status !== "Draft") {
        throw new Error(
          `Cannot delete payable with status "${item.status}". Only draft payables can be deleted.`
        );
      }
      return paymentsService.payable.delete(item.payableId);
    },
    onFinish: ({ successItems }) => {
      invalidateAfterMutation(successItems);
      onSuccess?.();
    }
  });

  return bulkAction.run(items).then(({ successItems, failedItems }) => ({
    total: items.length,
    processed: successItems.length + failedItems.length,
    successful: successItems.length,
    failed: failedItems.length
  }));
};

export const useBulkDeletePayables = () => {
  const runBulkAction = useForegroundBulkAction();

  return (items: Payable[], onSuccess?: () => void) => {
    const count = items.length;
    const pluralized = count > 1 ? "payables" : "payable";

    return runBulkAction({
      actionName: "Delete payables",
      itemCount: count,
      confirmDescription: `Are you sure you want to permanently delete ${count.toLocaleString()} draft ${pluralized}? This action cannot be undone.`,
      confirmLabel: "Delete",
      confirmDestructive: true,
      prepare: async () => items,
      process: async (data, onProgress) =>
        processDeletePayables(data, onProgress, onSuccess)
    });
  };
};
